import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '../lib/cn.ts'
import { avatarVariants, seedToColor, type AvatarVariants } from './Avatar.variants.ts'

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement>, AvatarVariants {
  /** 1–3 character label, e.g. "AP" for "Anna Petrenko". */
  initials: string
  /**
   * Background colour. If omitted, `seed` derives one deterministically from
   * `AVATAR_PALETTE`. If both are omitted, falls back to the first palette colour.
   */
  color?: string
  /** Used to derive a deterministic colour when `color` isn't given. Typically the entity id. */
  seed?: string
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { className, size, initials, color, seed, style, 'aria-label': ariaLabel, ...props },
  ref,
) {
  const bg = color ?? (seed ? seedToColor(seed) : undefined)
  return (
    <span
      ref={ref}
      aria-label={ariaLabel ?? initials}
      className={cn(avatarVariants({ size }), className)}
      style={bg ? { backgroundColor: bg, ...style } : style}
      {...props}
    >
      <span aria-hidden="true">{initials}</span>
    </span>
  )
})
